import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const IST_TZ = 'Asia/Kolkata';

// Backend stores IST-offset ISO strings (+05:30); legacy naive values were UTC.
// dayjs.utc() parses explicit offsets exactly and treats naive strings as UTC,
// so both formats render the correct IST wall time.
export const istDay = (d) => dayjs.utc(d).tz(IST_TZ);
export const nowIst = () => dayjs().tz(IST_TZ);

export const inr = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '\u20b90';
  return '\u20b9' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

export const inrCompact = (n) => {
  if (!n && n !== 0) return '\u20b90';
  const abs = Math.abs(n);
  if (abs >= 10000000) return '\u20b9' + (n / 10000000).toFixed(2) + ' Cr';
  if (abs >= 100000) return '\u20b9' + (n / 100000).toFixed(2) + ' L';
  if (abs >= 1000) return '\u20b9' + (n / 1000).toFixed(1) + 'k';
  return inr(n);
};

export const fmtDate = (d) => (d ? istDay(d).format('DD-MM-YYYY') : '\u2014');
export const fmtDateTime = (d) => (d ? istDay(d).format('DD-MM-YYYY, h:mm A') + ' IST' : '\u2014');
export const fmtPct = (n) => `${n ?? 0}%`;

// A follow-up is overdue when its date (IST) is before today (IST)
export const isOverdue = (d) => Boolean(d) && istDay(d).endOf('day').isBefore(nowIst());

// Hours since the lead was last touched (falls back to creation time), in IST
export const untouchedHours = (lead) => {
  const ref = lead?.last_touch || lead?.date_created;
  if (!ref) return 0;
  return nowIst().diff(istDay(ref), 'hour');
};

// Compact "52h" / "3d" label for the follow-up nudge badge
export const fmtUntouched = (hours) => (hours >= 72 ? `${Math.floor(hours / 24)}d` : `${hours}h`);

export const monthOptions = (count = 6) => {
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = nowIst().subtract(i, 'month');
    out.push({ value: d.format('YYYY-MM'), label: d.format('MMMM YYYY') });
  }
  return out;
};

export const STATUS_COLORS = {
  New: 'bg-blue-50 text-blue-700 border-blue-200',
  'Follow-Up Ongoing': 'bg-amber-50 text-amber-800 border-amber-200',
  'Meeting Follow-Up': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Proposal: 'bg-violet-50 text-violet-700 border-violet-200',
  'Proposal Follow-Up': 'bg-sky-50 text-sky-700 border-sky-200',
  Won: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Lost: 'bg-red-50 text-red-700 border-red-200',
};

export const LEAD_STATUSES = ['New', 'Follow-Up Ongoing', 'Meeting Follow-Up', 'Proposal', 'Proposal Follow-Up', 'Won', 'Lost'];
export const MEETING_STATUSES = ['Show', 'No-Show', 'Rescheduled By Us', 'Rescheduled By Them', 'Cancel', 'Disqualified (DQ)'];
export const LOST_REASONS = ['Price', 'Timing', 'Partner-Spouse', 'Competitor', 'Ghosted', 'Not Qualified'];
export const SOURCES = ['Website', 'LinkedIn', 'Referral', 'Cold Call', 'Instagram', 'Event', 'Other'];
export const DESIGNATIONS = ['Founder', 'Sales Head', 'Sales Executive', 'Sales Intern'];

export const ROLE_BADGES = {
  'Super Admin': 'bg-violet-50 text-violet-700 border-violet-200',
  Admin: 'bg-blue-50 text-blue-700 border-blue-200',
  Manager: 'bg-sky-50 text-sky-700 border-sky-200',
  Editor: 'bg-slate-50 text-slate-600 border-slate-200',
};

export const DESIGNATION_BADGES = {
  Founder: 'bg-violet-50 text-violet-700 border-violet-200',
  'Sales Head': 'bg-blue-50 text-blue-700 border-blue-200',
  'Sales Executive': 'bg-sky-50 text-sky-700 border-sky-200',
  'Sales Intern': 'bg-slate-50 text-slate-600 border-slate-200',
};
